import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, validate } from 'rut.js';
import { useFilterQueryParams } from '../../../hooks/useQueryParams';
import { StatusFilters, SearchInput, StatusFilter } from './Filters';
import { SigningProcessesTable } from './SigningProcessesTable';
import { getSigningProcesses, GetSigningProcessesParams, getSigningProcessesStatusCounts } from './backofficeServices';
import { config } from './config';
import { DocumentTypeOption } from './types';
import { MultiSelect } from '../../common/ui/Multiselect';

export function BackofficeContent(
  { isSuperuser, isReadOnly, documentTypes }:
  { isSuperuser: boolean, isReadOnly: boolean, documentTypes: DocumentTypeOption[] },
) {
  const {
    setFilter,
    paramsString,
    filters,
  } = useFilterQueryParams<GetSigningProcessesParams>(
    {
      search: '',
      status: ['PROGRESS'],
      page: 1,
      document_types: [],
    },
    {
      search: (value) => {
        if (typeof value === 'string') {
          return !value || validate(value);
        }
        return false;
      },
    }
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: [config.tanstack.baseSigningProcessesQueryKey, paramsString],
    queryFn: () => getSigningProcesses({ ...filters }),
    select: (response) => response.data,
    keepPreviousData: true,
    refetchInterval: config.tanstack.refetchInterval,
  });

  const {
    data: statusCounts,
    isLoading: isStatusCountsLoading,
  } = useQuery({
    queryKey: [config.tanstack.baseSigningProcessesStatusCountsQueryKey],
    queryFn: getSigningProcessesStatusCounts,
    select: (response) => response.data,
    refetchInterval: config.tanstack.refetchInterval,
  });

  const handleStatusFilterChange = (status: StatusFilter[]) => {
    setFilter('page', 1);
    setFilter('status', status);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter('page', 1);
    setFilter('search', e.target.value ? format(e.target.value) : '');
  };

  const handleDocumentTypesChange = (selectedOptions: DocumentTypeOption[]) => {
    setFilter('page', 1);
    setFilter('document_types', selectedOptions.map((opt) => opt.value));
  };

  return (
    <div className="d-flex flex-column gap-5">
      <h1 className="fs-5 text-start text-md-center text-primary"> Estado de firma de documentos</h1>

      <StatusFilters
        onChange={handleStatusFilterChange}
        statusCounts={statusCounts}
        isLoading={isStatusCountsLoading}
        initialFilters={filters.status}
      />

      <div>
        <h2 className="fs-6 text-primary mb-0 d-block" id="procesos-firma-heading">
          Estado de procesos de firma
        </h2>

        <div className="row g-2 justify-content-start justify-content-lg-end">
          <div className="col-12 col-lg-4">
            <MultiSelect
              options={documentTypes}
              onChange={handleDocumentTypesChange}
              placeholder="Selecciona tipo de documento"
              maxItemsToShow={0}
              value={documentTypes.filter((opt) => filters.document_types.includes(opt.value))}
            />
          </div>
          <div className="col-12 col-lg-3">
            <SearchInput
              value={filters.search}
              onChange={handleSearchChange}
            />
          </div>
        </div>

      </div>

      <SigningProcessesTable
        data={data || null}
        isLoading={isLoading}
        isError={isError}
        currentPage={filters.page}
        onPageChange={(page) => setFilter('page', page)}
        totalPages={data?.total_pages || 1}
        isSuperuser={isSuperuser}
        isReadOnly={isReadOnly}
      />
    </div>
  );
}
